/**
 * FRONT-533 "Delete everything": remove a document/file together with its
 * attached chat in one action.
 *
 * The asset trash is the PRIMARY, must-succeed step — it's what the user reads
 * as "deleted" and can recover from the Recycle bin (it's a soft-delete, not a
 * hard one). Clearing the attached conversation is best-effort cleanup that must
 * never block the delete or leave the asset in a half-deleted state. So the
 * whole action fails only if the trash itself fails; if the trash succeeds but
 * the chat clear does not, we still report success and let the caller soften the
 * confirmation wording.
 */
export interface DeleteEverythingDeps {
  /** Soft-delete the asset (document or file) into the Recycle bin. */
  trashAsset: () => Promise<unknown>;
  /** Clear the conversation attached to the asset. Best-effort. */
  clearChat: () => Promise<unknown>;
}

export type DeleteEverythingResult =
  | { ok: true; chatCleared: boolean }
  | { ok: false; error: Error };

export async function deleteEverything(
  deps: DeleteEverythingDeps,
): Promise<DeleteEverythingResult> {
  try {
    await deps.trashAsset();
  } catch (err) {
    // The asset could not be trashed — nothing was removed. Do NOT clear the
    // chat (that would discard the conversation while leaving the asset behind).
    return { ok: false, error: err as Error };
  }
  let chatCleared = true;
  try {
    await deps.clearChat();
  } catch {
    // The asset is already in the bin; a failed chat clear is non-fatal.
    chatCleared = false;
  }
  return { ok: true, chatCleared };
}
